import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { 
  Search, 
  MessageSquare, 
  Users,
  ArrowLeft,
  Image as ImageIcon,
  Edit,
  Clock,
  CheckCheck,
  MoreHorizontal
} from 'lucide-react';
import { apiClient as api } from '@/api/client';
import { usersAPI } from '@/api';
import { QUERY_KEYS } from '@/utils/constants';
import { handleSchoolId } from '@/utils/helpers';
import { useScopeFilter } from '@/hooks/useScopeFilter';
import { PageHeader } from '@/components/common/PageHeader';

interface Participant {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
}

interface Message {
  _id: string;
  content: string;
  message_type: 'text' | 'image' | 'system';
  image_url?: string;
  sender_id: Participant;
  createdAt: string;
  is_deleted?: boolean;
  last_edited_at?: string;
  read_by?: Participant[];
}

interface Chat {
  _id: string;
  participants: Participant[];
  last_message?: {
    _id: string;
    content: string;
    sender_id: Participant;
    createdAt: string;
    message_type?: string;
  };
  updatedAt: string;
  createdAt: string;
  unreadCount?: number;
}

interface ChatsResponse {
  message: string;
  data: Chat[];
  pagination: {
    total: number;
    page: number;
    pages: number;
  };
}

interface MessagesResponse {
  message: string;
  data: Message[];
}

const getAllChats = async (
  page: number = 1,
  limit: number = 20,
  search: string = '',
  schoolId?: string,
  campusId?: string
): Promise<ChatsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (search) params.append('search', search);
  if (schoolId) params.append('school_id', schoolId);
  if (campusId) params.append('campus_id', campusId);

  const response = await api.get(`/api/admin/chats/all?${params.toString()}`);
  return response.data;
};

const getChatMessages = async (chatId: string): Promise<MessagesResponse> => {
  const response = await api.get(`/api/admin/chats/${chatId}/messages`);
  return response.data;
};

export function MessagesPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const limit = 50;

  const { schoolId, campusId } = useScopeFilter();
  const scopeActive = Boolean(schoolId || campusId);

  // When a school/campus scope is active we pull a wide window and
  // filter/paginate on the client, so results are correct whether or not the
  // backend honors the school_id/campus_id params. With no scope we keep the
  // original server-side pagination untouched.
  const SCOPE_FETCH_LIMIT = 1000;
  const fetchPage = scopeActive ? 1 : currentPage;
  const fetchLimit = scopeActive ? SCOPE_FETCH_LIMIT : limit;

  // Reset to the first page whenever the scope or search changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [schoolId, campusId, searchTerm]);

  // Fetch all chats
  const {
    data: chatsData,
    isLoading: isLoadingChats,
    error: chatsError
  } = useQuery<ChatsResponse>({
    queryKey: ['adminChats', fetchPage, fetchLimit, searchTerm, schoolId ?? null, campusId ?? null],
    queryFn: () => getAllChats(fetchPage, fetchLimit, searchTerm, schoolId, campusId),
  });

  // Participant payloads omit school/campus, so when a scope is active we map
  // each user id to its school/campus via the users list (cached app-wide).
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: QUERY_KEYS.USERS,
    queryFn: usersAPI.getAllUsers,
    enabled: scopeActive,
    staleTime: 5 * 60 * 1000,
  });

  const userScopeMap = useMemo(() => {
    const map = new Map<
      string,
      {
        school_id?: string | { _id: string } | null;
        campus_id?: string | { _id: string } | null;
      }
    >();
    const list = Array.isArray(usersData) ? usersData : [];
    for (const u of list) {
      map.set(u._id, { school_id: u.school_id, campus_id: u.campus_id });
    }
    return map;
  }, [usersData]);

  // A conversation matches the scope only when EVERY participant belongs to the
  // selected school (and campus, if one is selected).
  const filteredChats = useMemo(() => {
    const all = chatsData?.data || [];
    if (!scopeActive) return all;
    return all.filter((chat) => {
      const parts = (chat.participants || []).filter(Boolean);
      if (parts.length === 0) return false;
      return parts.every((p) => {
        const u = userScopeMap.get(p._id);
        if (!u) return false;
        const schoolOk = schoolId
          ? handleSchoolId(u.school_id) === schoolId
          : true;
        const campusOk = campusId
          ? handleSchoolId(u.campus_id) === campusId
          : true;
        return schoolOk && campusOk;
      });
    });
  }, [chatsData, scopeActive, userScopeMap, schoolId, campusId]);

  const totalChats = scopeActive
    ? filteredChats.length
    : chatsData?.pagination.total || 0;
  const totalPages = scopeActive
    ? Math.max(1, Math.ceil(filteredChats.length / limit))
    : chatsData?.pagination.pages || 1;
  const pageChats = scopeActive
    ? filteredChats.slice((currentPage - 1) * limit, currentPage * limit)
    : filteredChats;
  const isLoadingList = isLoadingChats || (scopeActive && isLoadingUsers);

  // Fetch messages for selected chat
  const {
    data: messagesData,
    isLoading: isLoadingMessages
  } = useQuery<MessagesResponse>({
    queryKey: ['chatMessages', selectedChat?._id],
    queryFn: () => getChatMessages(selectedChat!._id),
    enabled: !!selectedChat,
  });

  // Auto-scroll to bottom when new messages are loaded
  useEffect(() => {
    if (messagesData && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesData]);

  const formatMessageTime = (date: string) => {
    const messageDate = new Date(date);
    if (isToday(messageDate)) {
      return format(messageDate, 'h:mm a');
    } else if (isYesterday(messageDate)) {
      return `Yesterday ${format(messageDate, 'h:mm a')}`;
    } else {
      return format(messageDate, 'MMM d, h:mm a');
    }
  };

  const formatLastMessageTime = (date: string) => {
    const messageDate = new Date(date);
    if (isToday(messageDate)) {
      return format(messageDate, 'h:mm a');
    } else if (isYesterday(messageDate)) {
      return 'Yesterday';
    } else {
      return format(messageDate, 'MMM d');
    }
  };

  const getParticipantName = (participant?: Participant) => {
    if (!participant) return 'Unknown user';
    return `${participant.first_name || ''} ${participant.last_name || ''}`.trim() || 'Unknown user';
  };

  const getParticipantNames = (participants: Participant[]) => {
    const valid = (participants || []).filter(Boolean);
    if (valid.length === 0) return 'Unknown conversation';
    if (valid.length === 2) {
      return valid.map(getParticipantName).join(' & ');
    }
    return valid
      .slice(0, 2)
      .map(getParticipantName)
      .join(', ') + (valid.length > 2 ? ` +${valid.length - 2}` : '');
  };

  const getParticipantInitials = (participant?: Participant) => {
    if (!participant) return '?';
    return `${participant.first_name?.[0] || ''}${participant.last_name?.[0] || ''}`.toUpperCase() || '?';
  };

  const getLastMessagePreview = (message: Chat['last_message']) => {
    if (!message) return 'No messages yet';
    
    if (message.message_type === 'image') {
      return '📷 Photo';
    }
    if (message.message_type === 'system') {
      return '🔔 ' + message.content;
    }
    
    return message.content.length > 50 
      ? message.content.substring(0, 50) + '...'
      : message.content;
  };

  const renderMessageBubble = (message: Message, isOwnMessage: boolean = false) => {
    if (message.is_deleted) {
      return (
        <div className="flex justify-center my-2">
          <div className="text-muted-foreground italic text-sm bg-muted px-3 py-1 rounded-full">
            🗑️ This message was deleted
          </div>
        </div>
      );
    }

    if (message.message_type === 'system') {
      return (
        <div className="flex justify-center my-4">
          <Badge variant="secondary" className="text-xs px-3 py-1">
            {message.content}
          </Badge>
        </div>
      );
    }

    const bubbleClass = isOwnMessage
      ? 'bg-primary text-primary-foreground ml-auto'
      : 'bg-card text-card-foreground border border-border';
    
    const containerClass = isOwnMessage ? 'justify-end' : 'justify-start';

    return (
      <div className={`flex ${containerClass} mb-3 group`}>
        {/* Avatar for incoming messages */}
        {!isOwnMessage && (
          <Avatar className="h-8 w-8 mr-3 mt-auto">
            <AvatarImage src={message.sender_id?.avatar_url} />
            <AvatarFallback className="bg-muted text-muted-foreground text-xs">
              {getParticipantInitials(message.sender_id)}
            </AvatarFallback>
          </Avatar>
        )}

        <div className={`max-w-xs lg:max-w-md`}>
          {/* Sender name for incoming messages */}
          {!isOwnMessage && (
            <div className="text-xs text-muted-foreground mb-1 ml-2">
              {getParticipantName(message.sender_id)}
            </div>
          )}

          <div className={`rounded-2xl px-4 py-2 ${bubbleClass} shadow-sm`}>
            {message.message_type === 'image' && message.image_url ? (
              <div className="space-y-2">
                <div className="relative group">
                  <img
                    src={message.image_url}
                    alt="Chat image"
                    className="rounded-lg max-w-full cursor-pointer"
                    onClick={() => window.open(message.image_url, '_blank')}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                {message.content && (
                  <p className="text-sm leading-relaxed break-words">
                    {message.content}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm leading-relaxed break-words">
                {message.content}
              </p>
            )}

            {/* Time and edit indicator */}
            <div className={`text-xs mt-1 flex items-center gap-1 ${
              isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'
            }`}>
              <span>{formatMessageTime(message.createdAt)}</span>
              {message.last_edited_at && (
                <Edit className="h-3 w-3" />
              )}
              {isOwnMessage && message.read_by && message.read_by.length > 0 && (
                <CheckCheck className="h-3 w-3" />
              )}
            </div>
          </div>
        </div>

        {/* Avatar for outgoing messages */}
        {isOwnMessage && (
          <Avatar className="h-8 w-8 ml-3 mt-auto">
            <AvatarImage src={message.sender_id?.avatar_url} />
            <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
              {getParticipantInitials(message.sender_id)}
            </AvatarFallback>
          </Avatar>
        )}
      </div>
    );
  };

  // Show chat list view
  if (!selectedChat) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6">
          <PageHeader
            title="Messages & Chats"
            description="View and manage all user conversations"
            className="mb-6"
          />

          {/* Search and Stats */}
          <div className="mb-6 grid gap-4 md:grid-cols-4">
            <Card className="md:col-span-1">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <MessageSquare className="h-8 w-8 text-blue-500" />
                  <div className="text-right">
                    <p className="text-2xl font-bold">{totalChats}</p>
                    <p className="text-sm text-muted-foreground">Total Chats</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
                <Input
                  placeholder="Search by user name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Chat List */}
          <Card>
            <CardContent className="p-0">
              {isLoadingList ? (
                <div className="space-y-4 p-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                      <Skeleton className="h-4 w-16" />
                    </div>
                  ))}
                </div>
              ) : chatsError ? (
                <div className="p-6 text-center">
                  <p className="text-red-500">Error loading chats. Please try again.</p>
                </div>
              ) : pageChats.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No conversations found</h3>
                  <p className="text-muted-foreground">
                    {scopeActive
                      ? 'No conversations match the selected school/campus.'
                      : 'Try adjusting your search terms'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {pageChats.map((chat) => (
                    <div 
                      key={chat._id} 
                      className="p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedChat(chat)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {/* Chat Avatar */}
                          <div className="relative">
                            {chat.participants.length <= 2 ? (
                              <Avatar className="h-12 w-12">
                                <AvatarImage src={chat.participants[0]?.avatar_url} />
                                <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                                  {getParticipantInitials(chat.participants[0])}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <div className="relative">
                                <Avatar className="h-12 w-12">
                                  <AvatarImage src={chat.participants[0]?.avatar_url} />
                                  <AvatarFallback className="bg-green-100 text-green-700 font-semibold">
                                    {getParticipantInitials(chat.participants[0])}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-foreground text-background text-xs rounded-full flex items-center justify-center">
                                  {chat.participants.length}
                                </div>
                              </div>
                            )}
                            
                            {/* Online indicator */}
                            <div className="absolute -bottom-0 -right-0 h-3 w-3 bg-green-400 border-2 border-card rounded-full"></div>
                          </div>

                          {/* Chat Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground truncate">
                                {getParticipantNames(chat.participants)}
                              </h3>
                              {chat.participants.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  {chat.participants.length}
                                </Badge>
                              )}
                            </div>
                            {chat.last_message && (
                              <p className="text-sm text-muted-foreground truncate">
                                <span className="font-medium">
                                  {chat.last_message.sender_id?.first_name}:
                                </span>{' '}
                                {getLastMessagePreview(chat.last_message)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">
                              {chat.last_message && formatLastMessageTime(chat.last_message.createdAt)}
                            </p>
                            {chat.unreadCount && chat.unreadCount > 0 && (
                              <Badge 
                                variant="default" 
                                className="mt-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-primary"
                              >
                                {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="flex items-center px-4 text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // Show individual chat view with bubbles
  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-6rem)] lg:h-[calc(100vh-7rem)] overflow-hidden rounded-lg border border-border">
        {/* Chat Header */}
        <div className="bg-card border-b border-border p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedChat(null)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>

              {/* Chat Avatar */}
              {selectedChat.participants.length <= 2 ? (
                <Avatar className="h-10 w-10">
                  <AvatarImage src={selectedChat.participants[0]?.avatar_url} />
                  <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                    {getParticipantInitials(selectedChat.participants[0])}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={selectedChat.participants[0]?.avatar_url} />
                  <AvatarFallback className="bg-green-100 text-green-700 font-semibold">
                    {getParticipantInitials(selectedChat.participants[0])}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-foreground text-background text-xs rounded-full flex items-center justify-center">
                    {selectedChat.participants.length}
                  </div>
                </div>
              )}
              
              <div>
                <h2 className="font-semibold text-foreground">
                  {getParticipantNames(selectedChat.participants)}
                </h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span>{selectedChat.participants.length} participants</span>
                  <span>•</span>
                  <Clock className="h-3 w-3" />
                  <span>
                    Created {formatDistanceToNow(new Date(selectedChat.createdAt), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {messagesData?.data.length || 0} messages
              </Badge>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Messages Area with Bubbles */}
        <div className="flex-1 bg-muted/30 overflow-hidden">
          <ScrollArea className="h-full">
            {isLoadingMessages ? (
              <div className="p-6 space-y-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                    <Skeleton className={`h-12 ${i % 2 === 0 ? 'w-2/3' : 'w-1/2'} rounded-2xl`} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4">
                {messagesData?.data.map((message, index) => {
                  const isFirstMessageOfDay = index === 0 || 
                    !isToday(new Date(message.createdAt)) && 
                    (index === 0 || format(new Date(message.createdAt), 'yyyy-MM-dd') !== 
                     format(new Date(messagesData.data[index - 1].createdAt), 'yyyy-MM-dd'));

                  // For demo purposes, alternate between left and right bubbles
                  // In real app, you'd check if message.sender_id matches current admin user
                  const isOwnMessage = index % 3 === 1; // Simulate some messages being from admin

                  return (
                    <div key={message._id}>
                      {/* Date separator */}
                      {isFirstMessageOfDay && (
                        <div className="flex items-center justify-center my-6">
                          <Separator className="flex-1" />
                          <Badge variant="secondary" className="mx-4 text-xs px-3 py-1">
                            {format(new Date(message.createdAt), 'EEEE, MMMM d, yyyy')}
                          </Badge>
                          <Separator className="flex-1" />
                        </div>
                      )}
                      
                      {renderMessageBubble(message, isOwnMessage)}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </DashboardLayout>
  );
}